import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import useWidth from '@components/Hooks/useWidth';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import { ScreenHeaderTagConfig } from '@components/v2/layout/ScreenHeader/screenHeader.types';
import Breadcrumb from '@components/v2/ui/Breadcrumb/Breadcrumb';
import { BreadcrumbItem } from '@components/v2/ui/Breadcrumb/breadcrumb.types';
import Loading from '@components/v2/ui/Loading/Loading';
import StatusIcon from '@components/v2/ui/StatusChip/components/StatusIcon';
import { Box, Stack } from '@mui/material';
import { usePublicApplication } from '../../../hooks/usePublicApplication';
import LangEnum from '../../../lang/LangEnum';
import { APP_BAR_HEIGHT_DESKTOP, APP_BAR_HEIGHT_MOBILE } from '../../../utils/constants';
import useHideContactBrandButton from '../../context/ApplicationContext/hooks/useHideContactBrandButton';
import { isOfflineStatus } from '../../offline/domain/offlineOrder/offlineOrder.types';
import { OrderContextProvider, useOrderContext } from '../../orders/contexts/Order.context';
import { OrderActionsContextProvider } from '../../orders/contexts/OrderActions.context';
import { OrderCalculateContextProvider } from '../../orders/contexts/OrderCalculate.context';
import { OrderConditionContextProvider } from '../../orders/contexts/OrderCondition.context';
import { OrderEditSectionsContextProvider } from '../../orders/edit/sections/OrderEditSections.context';
import OrderEditPageSummary from '../../orders/edit/summary/components/OrderEditPageSummary';
import OrderSummaryEndActions from '../../orders/edit/summary/components/actions/OrderSummaryEndActions';
import { ORDER_STATUS_CHIP_CONFIG } from '../../orders/list/components/orderStatusChip.constants';
import OrderModals from '../../orders/modals/OrderModals';
import OrderObjectSummarySection from '../../orders/pages/OrderObjectPage/components/OrderObjectDetailsSections/components/OrderObjectSummarySection';
import OrderProductsViewButton from '../../orders/pages/OrderObjectPage/components/OrderProductsViewButton/OrderProductsViewButton';
import OrderProducts from '../../orders/products/OrderProducts';
import { ProductLockProvider } from '../../products/products-details-v2/hooks/useProductLock';
import { useTranslation } from '../../translation/TranslationContext';
import useSharedEntities from '../hooks/useSharedEntities';
import ValidatePage from './ValidatePage';

const OrderReadOnlyPageContent = () => {
  const { translate } = useTranslation();
  const { order, canEditOrder } = useOrderContext();
  const { isPublicAccess } = useSharedEntities({});
  usePublicApplication();
  const { mobileWidth, isCustomizedTabletDown } = useWidth();
  const appBarHeight = mobileWidth ? APP_BAR_HEIGHT_MOBILE : APP_BAR_HEIGHT_DESKTOP;
  const isReadOnlyView = !canEditOrder || isPublicAccess;

  useHideContactBrandButton();

  if (!order) {
    return (
      <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Loading />
      </Stack>
    );
  }

  const statusConfig = isOfflineStatus(order.status)
    ? undefined
    : ORDER_STATUS_CHIP_CONFIG[order.status];

  const tags: ScreenHeaderTagConfig[] = statusConfig
    ? [
        {
          label: statusConfig.label,
          icon: <StatusIcon icon={statusConfig.icon} color={statusConfig.color} size="small" />,
        },
      ]
    : [];

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: `${translate(LangEnum.GLOBAL_ORDER)} ${order.code}` },
  ];

  return (
    <Box sx={{ height: `calc(100vh - ${appBarHeight}px)` }}>
      <ObjectScreen
        objectId={order.id}
        objectType="order"
        defaultTab="products"
        defaultDetailsOpen={!isCustomizedTabletDown}
        isDetailsPanelInlineOnTablet
      >
        <ObjectScreen.Header
          tags={tags}
          actions={<OrderSummaryEndActions />}
          breadcrumb={<Breadcrumb items={breadcrumbItems} />}
        />

        <ObjectScreen.Content>
          {() => (
            <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
              <ObjectScreen.Controls>
                <OrderProductsViewButton />
              </ObjectScreen.Controls>

              <Box sx={{ flex: 1 }}>
                <OrderProducts
                  orderId={order.id}
                  orderStatus={order.status}
                  viewMode={SelectionMenuViewMode.EDIT}
                  orderBrandHasIntegrations={!!order.hasBrandIntegrations}
                />
              </Box>

              <Box sx={{ position: 'sticky', bottom: 0, zIndex: 10 }}>
                <OrderEditPageSummary isSuggested={isReadOnlyView} />
              </Box>
            </Box>
          )}
        </ObjectScreen.Content>

        <ObjectScreen.DetailsPanel>
          <OrderObjectSummarySection isReadOnlyView={isReadOnlyView} />
        </ObjectScreen.DetailsPanel>
      </ObjectScreen>
    </Box>
  );
};

const OrderReadOnlyPage = () => (
  <ValidatePage>
    <OrderActionsContextProvider>
      <OrderContextProvider>
        <OrderConditionContextProvider>
          <OrderCalculateContextProvider>
            <OrderModals />
            <OrderEditSectionsContextProvider>
              <ProductLockProvider>
                <OrderReadOnlyPageContent />
              </ProductLockProvider>
            </OrderEditSectionsContextProvider>
          </OrderCalculateContextProvider>
        </OrderConditionContextProvider>
      </OrderContextProvider>
    </OrderActionsContextProvider>
  </ValidatePage>
);

export default OrderReadOnlyPage;
